import React, { useState, useRef, useCallback } from 'react';
import { Badge, Typography } from 'antd';
import { CloseOutlined, DragOutlined } from '@ant-design/icons';
import { useFeatureFlagStore } from 'zustand/featureFlag/useFeatureFlagStore';

import * as S from './styles';

const { Text, Title } = Typography;

/**
 * Componente de debug para feature flags
 * Exibe apenas em desenvolvimento e quando há feature flags carregados
 */
export function FeatureFlagDebugPanel() {
  const [isVisible, setIsVisible] = useState(true);
  const [position, setPosition] = useState({ x: window.innerWidth - 336, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const dragRef = useRef<HTMLDivElement>(null);
  const { flags, isLoading, isError } = useFeatureFlagStore();

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - 100;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  if (isLoading || isError || !flags?.length || !isVisible) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <S.DebugContainer 
      ref={dragRef}
      $x={position.x}
      $y={position.y}
      $isDragging={isDragging}
    >
      <S.DebugHeader onMouseDown={handleMouseDown}>
        <S.DragHandle>
          <DragOutlined style={{ color: '#8c8c8c', fontSize: '12px' }} />
          <Title level={5} style={{ margin: 0 }}>
            🚩 Feature Flags (Debug)
          </Title>
        </S.DragHandle>
        <S.ButtonsContainer>
          <S.CloseButton 
            icon={<CloseOutlined />} 
            onClick={handleClose}
            type="text"
            size="small"
          />
        </S.ButtonsContainer>
      </S.DebugHeader>
      
      {flags.map((flag) => (
        <S.FeatureFlagItem key={flag.name}>
          <Text style={{ fontSize: '12px' }}>{flag.name}</Text>
          <Badge 
            status={flag.isEnabled ? 'success' : 'default'} 
            text={flag.isEnabled ? 'ON' : 'OFF'}
          />
        </S.FeatureFlagItem>
      ))}
    </S.DebugContainer>
  );
}
